import React from "react";
import {
  MdDownload,
  MdPeopleAlt,
  MdQuiz,
  MdSchedule,
  MdTrendingUp,
  MdCheckCircle,
  MdVisibility,
} from "react-icons/md";
import Card from "components/card";
import EmptyState from "components/ui/EmptyState";
import SlideOver from "components/ui/SlideOver";
import Select from "components/ui/Select";
import DataTablePagination from "components/ui/DataTablePagination";
import { Skeleton } from "components/ui/Skeleton";
import { useAppLanguage } from "hooks/useAppLanguage";
import { useCourse } from "../../context/CourseContext";
import {
  exportStudentsProgressCsv,
  learningAnalyticsService,
  type CourseAnalytics,
  type ProgressStatus,
  type StudentCourseProgressDetail,
  type StudentProgressRow,
} from "services/learningAnalyticsService";
import {
  DATA_TABLE_BODY_ROW,
  DATA_TABLE_HEADER_ROW,
  DATA_TABLE_SCROLL,
  dataTableBodyCellClass,
  dataTableHeaderCellClass,
} from "lib/dataTableClasses";

function useDebounced<T>(value: T, delay = 350) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const STATUS_STYLES: Record<ProgressStatus, string> = {
  not_started: "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-200",
  in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200",
  completed: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200",
  inactive: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200",
};

function statusLabel(status: ProgressStatus, isArabic: boolean) {
  const map: Record<ProgressStatus, { ar: string; en: string }> = {
    not_started: { ar: "لم يبدأ", en: "Not started" },
    in_progress: { ar: "قيد التقدم", en: "In progress" },
    completed: { ar: "مكتمل", en: "Completed" },
    inactive: { ar: "غير نشط", en: "Inactive" },
  };
  return isArabic ? map[status].ar : map[status].en;
}

function formatDate(value: string | null | undefined, isArabic: boolean) {
  if (!value) return isArabic ? "—" : "—";
  return value.replace("T", " ").slice(0, 16);
}

function SummaryCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  tone: string;
}) {
  return (
    <div className={`rounded-2xl border border-gray-200 bg-gradient-to-br p-4 shadow-sm dark:border-white/10 ${tone}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 dark:bg-navy-800">{icon}</div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-bold text-gray-800 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="h-2 w-full min-w-[80px] overflow-hidden rounded-full bg-gray-200 dark:bg-white/10">
      <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

function StudentDetailPanel({
  detail,
  isArabic,
}: {
  detail: StudentCourseProgressDetail;
  isArabic: boolean;
}) {
  const studentName = detail?.student?.name ?? (isArabic ? "طالب" : "Student");
  const progressPct = detail?.course_progress?.progress_percentage ?? 0;
  const completedItems = detail?.course_progress?.completed_items_count ?? 0;
  const totalItems = detail?.course_progress?.total_items_count ?? 0;
  const videosWatched = detail?.summary?.videos_watched_count ?? 0;
  const filesDone =
    (detail?.summary?.files_completed_count ?? 0) +
    (detail?.summary?.images_completed_count ?? 0);
  const items = Array.isArray(detail?.items) ? detail.items : [];
  const quizAttempts = Array.isArray(detail?.quiz_attempts) ? detail.quiz_attempts : [];

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
        <p className="text-sm font-semibold text-gray-800 dark:text-white">{studentName}</p>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs text-gray-500">{isArabic ? "التقدم" : "Progress"}</p>
            <p className="font-bold">{progressPct}%</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">{isArabic ? "العناصر" : "Items"}</p>
            <p className="font-bold">
              {completedItems}/{totalItems}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">{isArabic ? "فيديوهات" : "Videos"}</p>
            <p className="font-bold">{videosWatched}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">{isArabic ? "ملفات/صور" : "Files/Images"}</p>
            <p className="font-bold">{filesDone}</p>
          </div>
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold text-gray-800 dark:text-white">
          {isArabic ? "تقدم العناصر" : "Item progress"}
        </h4>
        {items.length === 0 ? (
          <p className="text-sm text-gray-500">{isArabic ? "لا يوجد محتوى" : "No content"}</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => {
              const pct = item?.progress?.progress_percentage ?? 0;
              const progressSeconds = item?.progress?.progress_seconds;
              const durationSeconds = item?.progress?.duration_seconds;
              return (
                <li key={item.item_id} className="rounded-lg border border-gray-200 p-3 dark:border-white/10">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-gray-800 dark:text-white">
                        {item.item_title ?? "—"}
                      </p>
                      <p className="text-xs text-gray-500">{item.item_type}</p>
                    </div>
                    <span className="shrink-0 text-xs font-semibold text-brand-600">{pct}%</span>
                  </div>
                  {item.item_type === "video" && progressSeconds != null ? (
                    <p className="mt-1 text-xs text-gray-500">
                      {isArabic ? "مشاهدة" : "Watched"}: {progressSeconds}s
                      {durationSeconds ? ` / ${durationSeconds}s` : ""}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold text-gray-800 dark:text-white">
          {isArabic ? "محاولات الاختبارات" : "Quiz attempts"}
        </h4>
        {quizAttempts.length === 0 ? (
          <p className="text-sm text-gray-500">{isArabic ? "لا توجد محاولات" : "No attempts"}</p>
        ) : (
          <ul className="space-y-2">
            {quizAttempts.map((attempt) => (
              <li key={attempt.id} className="rounded-lg border border-gray-200 p-3 dark:border-white/10">
                <p className="text-sm font-medium text-gray-800 dark:text-white">
                  {attempt.quiz_title ?? "Quiz"}
                </p>
                <p className="text-xs text-gray-500">
                  {isArabic ? "الدرجة" : "Score"}: {attempt.score ?? 0}% ·{" "}
                  {attempt.correct_answers_count ?? 0}/{attempt.total_questions ?? 0}
                </p>
                <p className="text-xs text-gray-400">{formatDate(attempt.submitted_at, isArabic)}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function StudentProgressPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const { course } = useCourse();

  const [analytics, setAnalytics] = React.useState<CourseAnalytics | null>(null);
  const [rows, setRows] = React.useState<StudentProgressRow[]>([]);
  const [page, setPage] = React.useState(1);
  const [meta, setMeta] = React.useState({ current_page: 1, last_page: 1, per_page: 20, total: 0, from: null as number | null, to: null as number | null });
  const [search, setSearch] = React.useState("");
  const [progressStatus, setProgressStatus] = React.useState<ProgressStatus | "">("");
  const [accessStatus, setAccessStatus] = React.useState<"" | "active" | "not_started" | "expired">("");
  const [loading, setLoading] = React.useState(true);
  const [tableLoading, setTableLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detail, setDetail] = React.useState<StudentCourseProgressDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);

  const debouncedSearch = useDebounced(search);

  React.useEffect(() => {
    let cancelled = false;
    async function loadAnalytics() {
      setLoading(true);
      setError(null);
      try {
        const data = await learningAnalyticsService.instructorCourseAnalytics(course.id);
        if (!cancelled) setAnalytics(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message ?? (isArabic ? "تعذر تحميل التحليلات" : "Failed to load analytics"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadAnalytics();
    return () => {
      cancelled = true;
    };
  }, [course.id, isArabic]);

  React.useEffect(() => {
    let cancelled = false;
    async function loadRows() {
      setTableLoading(true);
      try {
        const result = await learningAnalyticsService.instructorStudentsProgress(course.id, {
          page,
          per_page: 20,
          search: debouncedSearch.trim() || undefined,
          progress_status: progressStatus || undefined,
          access_status: accessStatus || undefined,
        });
        if (!cancelled) {
          setRows(result.data);
          setMeta(result.meta);
        }
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setTableLoading(false);
      }
    }
    void loadRows();
    return () => {
      cancelled = true;
    };
  }, [course.id, page, debouncedSearch, progressStatus, accessStatus]);

  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, progressStatus, accessStatus]);

  const openDetail = async (studentId: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await learningAnalyticsService.instructorStudentCourseProgress(course.id, studentId);
      setDetail(data);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading && !analytics) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        title={isArabic ? "تعذر تحميل التحليلات" : "Failed to load analytics"}
        description={error}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        <SummaryCard
          icon={<MdPeopleAlt className="h-5 w-5 text-brand-600" />}
          label={isArabic ? "إجمالي الطلاب" : "Total students"}
          value={analytics?.total_students ?? 0}
          tone="from-brand-400/10 to-brand-400/5"
        />
        <SummaryCard
          icon={<MdTrendingUp className="h-5 w-5 text-indigo-600" />}
          label={isArabic ? "متوسط التقدم" : "Average progress"}
          value={`${analytics?.average_progress_percentage ?? 0}%`}
          tone="from-indigo-400/10 to-indigo-400/5"
        />
        <SummaryCard
          icon={<MdCheckCircle className="h-5 w-5 text-emerald-600" />}
          label={isArabic ? "طلاب مكتملون" : "Completed students"}
          value={analytics?.completed_students ?? 0}
          tone="from-emerald-400/10 to-emerald-400/5"
        />
        <SummaryCard
          icon={<MdQuiz className="h-5 w-5 text-amber-600" />}
          label={isArabic ? "متوسط الاختبارات" : "Average quiz score"}
          value={`${analytics?.average_quiz_score ?? 0}%`}
          tone="from-amber-400/10 to-amber-400/5"
        />
        <SummaryCard
          icon={<MdSchedule className="h-5 w-5 text-gray-600" />}
          label={isArabic ? "آخر نشاط" : "Last activity"}
          value={formatDate(analytics?.last_activity_at, isArabic)}
          tone="from-gray-400/10 to-gray-400/5"
        />
      </div>

      {!analytics?.has_content ? (
        <Card extra="p-4">
          <p className="text-sm text-gray-500">
            {isArabic ? "لا يوجد محتوى تعليمي في هذا الكورس بعد." : "This course has no learnable content yet."}
          </p>
        </Card>
      ) : null}

      <Card extra="p-4">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isArabic ? "بحث عن طالب..." : "Search student..."}
              className="rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-900"
            />
            <Select
              value={progressStatus || null}
              onChange={(next) =>
                setProgressStatus((next === null ? "" : String(next)) as ProgressStatus | "")
              }
              options={[
                { value: "not_started", label: statusLabel("not_started", isArabic) },
                { value: "in_progress", label: statusLabel("in_progress", isArabic) },
                { value: "completed", label: statusLabel("completed", isArabic) },
                { value: "inactive", label: statusLabel("inactive", isArabic) },
              ]}
              isArabic={isArabic}
              emptyValue=""
              placeholder={isArabic ? "كل الحالات" : "All statuses"}
            />
            <Select
              value={accessStatus || null}
              onChange={(next) =>
                setAccessStatus((next === null ? "" : String(next)) as typeof accessStatus)
              }
              options={[
                { value: "active", label: isArabic ? "نشط" : "Active" },
                { value: "not_started", label: isArabic ? "لم يبدأ" : "Not started" },
                { value: "expired", label: isArabic ? "منتهي" : "Expired" },
              ]}
              isArabic={isArabic}
              emptyValue=""
              placeholder={isArabic ? "كل الوصول" : "All access"}
            />
          </div>
          <div className="flex flex-col items-stretch gap-1 sm:items-end">
            <button
              type="button"
              onClick={() => exportStudentsProgressCsv(rows, isArabic)}
              disabled={rows.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-white/10 dark:text-white"
            >
              <MdDownload className="h-4 w-4" />
              {isArabic ? "تصدير CSV" : "Export CSV"}
            </button>
            <p className="text-xs text-gray-500">
              {isArabic ? "يتم تصدير الصفوف المعروضة حاليًا فقط." : "Exports the currently loaded rows only."}
            </p>
          </div>
        </div>

        <div className={DATA_TABLE_SCROLL}>
          <table className="w-full min-w-[900px]" dir={isArabic ? "rtl" : "ltr"}>
            <thead>
              <tr className={DATA_TABLE_HEADER_ROW}>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "الطالب" : "Student"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "التقدم" : "Progress"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "العناصر" : "Items"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "متوسط الاختبار" : "Quiz avg"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "آخر نشاط" : "Last activity"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "الحالة" : "Status"}</th>
                <th className={dataTableHeaderCellClass(isArabic)}>{isArabic ? "إجراءات" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-sm text-gray-500">
                    {isArabic ? "جاري التحميل..." : "Loading..."}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-sm text-gray-500">
                    {isArabic ? "لا يوجد طلاب" : "No students found"}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.student_id} className={DATA_TABLE_BODY_ROW}>
                    <td className={dataTableBodyCellClass(isArabic)}>
                      <p className="font-medium text-gray-800 dark:text-white">{row.student_name}</p>
                    </td>
                    <td className={dataTableBodyCellClass(isArabic)}>
                      <div className="flex items-center gap-2">
                        <ProgressBar value={row.progress_percentage} />
                        <span className="text-xs font-semibold">{row.progress_percentage}%</span>
                      </div>
                    </td>
                    <td className={dataTableBodyCellClass(isArabic)}>
                      {row.completed_items_count}/{row.total_items_count}
                    </td>
                    <td className={dataTableBodyCellClass(isArabic)}>
                      {row.quizzes_attempted_count > 0 ? `${row.average_quiz_score}%` : "—"}
                    </td>
                    <td className={dataTableBodyCellClass(isArabic)}>{formatDate(row.last_accessed_at, isArabic)}</td>
                    <td className={dataTableBodyCellClass(isArabic)}>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[row.status]}`}>
                        {statusLabel(row.status, isArabic)}
                      </span>
                    </td>
                    <td className={dataTableBodyCellClass(isArabic)}>
                      <button
                        type="button"
                        onClick={() => openDetail(row.student_id)}
                        className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-100 dark:bg-brand-500/10 dark:text-brand-200"
                      >
                        <MdVisibility className="h-3.5 w-3.5" />
                        {isArabic ? "التفاصيل" : "Details"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <DataTablePagination
          isArabic={isArabic}
          page={meta.current_page}
          totalPages={meta.last_page}
          fromItem={meta.from ?? 0}
          toItem={meta.to ?? 0}
          totalItems={meta.total}
          onPageChange={setPage}
          className="mt-4"
        />
      </Card>

      <SlideOver
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={isArabic ? "تفاصيل تقدم الطالب" : "Student progress details"}
        width="lg"
      >
        {detailLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : detail ? (
          <StudentDetailPanel detail={detail} isArabic={isArabic} />
        ) : (
          <p className="text-sm text-gray-500">{isArabic ? "تعذر تحميل التفاصيل" : "Failed to load details"}</p>
        )}
      </SlideOver>
    </div>
  );
}
